import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { ShipmentBatchList } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import { BATCH_STATUS_LABEL, BATCH_STATUS_TONE, kgLabel, providerLabel } from "./labels";

type StatusFilter = "" | "open" | "closed";

/**
 * Vendas › Logística › Remessas (T3.4): agrupamento de objetos para postagem (a antiga PLP dos Correios). Fechar a
 * remessa gera a lista de postagem e as etiquetas num PDF só.
 */
export function BatchesPage(): ReactElement {
  const { companyId, api } = useSession();
  const [status, setStatus] = useState<StatusFilter>("");
  const list = useInfiniteQuery({
    queryKey: ["logistics", companyId, "batches", status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ShipmentBatchList>(
        `/logistics/batches?${new URLSearchParams({
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <ContentHeader
        title="Remessas de postagem"
        breadcrumb={["Vendas", "Logística", "Remessas"]}
        actions={
          <Link to="/vendas/logistica" className="btn btn-outline-secondary">
            Objetos de postagem
          </Link>
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="lg-batch-status">
              Situação
            </label>
            <select
              id="lg-batch-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="">Todas</option>
              <option value="open">{BATCH_STATUS_LABEL.open}</option>
              <option value="closed">{BATCH_STATUS_LABEL.closed}</option>
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma remessa"
            description="Selecione objetos com etiqueta gerada na lista de objetos e use “Criar remessa”."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Remessas de postagem">
                <thead>
                  <tr>
                    <th scope="col">Remessa</th>
                    <th scope="col">Integração</th>
                    <th scope="col" className="text-end">
                      Objetos
                    </th>
                    <th scope="col" className="text-end">
                      Peso
                    </th>
                    <th scope="col" className="text-end">
                      Valor declarado
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <Link to="/vendas/logistica/remessas/$id" params={{ id: b.id }}>
                          {b.number}
                        </Link>
                        <div className="small text-muted">{dateTimeBR(b.createdAt)}</div>
                      </td>
                      <td>
                        {b.integrationName}
                        <div className="small text-muted">{providerLabel(b.provider)}</div>
                      </td>
                      <td className="text-end">{b.shipmentsCount}</td>
                      <td className="text-end">{kgLabel(b.weightG)}</td>
                      <td className="text-end">{formatBRL(b.declaredValueCents)}</td>
                      <td>
                        <StatusBadge tone={BATCH_STATUS_TONE[b.status]}>
                          {BATCH_STATUS_LABEL[b.status]}
                        </StatusBadge>
                        {b.closedAt && <div className="small text-muted">{dateTimeBR(b.closedAt)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
